import type { RefObject } from "react";
import { useCallback, useEffect, useRef } from "react";

/**
 * Configuration options for the usePreventScrollBounceHook hook.
 *
 * @public
 */
export interface UsePreventScrollBounceHookOptions {

    /**
     * Enables or disables the scroll bounce prevention.
     * @defaultValue true
     */
    readonly "isEnabled"?: boolean;
}

/**
 * Prevents scroll bounce on touch devices for the provided scrollable element.
 *
 * @example
 * ```tsx
 * const listRef = useRef<HTMLDivElement>(null);
 * usePreventScrollBounceHook(listRef);
 * ```
 *
 * @param elementRef - React ref pointing to the scrollable element.
 * @param options - Hook configuration options.
 *
 * @returns void
 */
export const usePreventScrollBounceHook = <ElementType extends HTMLElement>(
    elementRef: RefObject<ElementType | null>,
    options: UsePreventScrollBounceHookOptions = {},
): void => {
    const startYRef = useRef<number>( 0 );
    const { isEnabled = true } = options;

    const handleTouchStart = useCallback(
        ( event: TouchEvent ) => {
            if ( !isEnabled ) {
                return;
            }

            const firstTouch = event.touches[0];
            if ( firstTouch !== undefined ) {
                startYRef.current = firstTouch.clientY;
            }
        },
        [ isEnabled ],
    );

    const handleTouchMove = useCallback(
        ( event: TouchEvent ) => {
            if ( !isEnabled ) {
                return;
            }

            const element = elementRef.current;
            if ( element === null ) {
                return;
            }

            if ( element.scrollHeight <= element.clientHeight ) {
                event.preventDefault();

                return;
            }

            const firstTouch = event.touches[0];

            if ( firstTouch === undefined ) {
                return;
            }

            const deltaY = firstTouch.clientY - startYRef.current;
            const scrollTop = element.scrollTop;
            const isAtTop = scrollTop <= 0;
            const isAtBottom =
                scrollTop + element.clientHeight >= element.scrollHeight;

            if ( ( isAtTop && deltaY > 0 ) || ( isAtBottom && deltaY < 0 ) ) {
                event.preventDefault();
            }
        },
        [ elementRef, isEnabled ],
    );

    useEffect( () => {
        const element = elementRef.current;
        if ( element === null || !isEnabled ) {
            return;
        }

        element.addEventListener( "touchstart", handleTouchStart, { "passive": false } );
        element.addEventListener( "touchmove", handleTouchMove, { "passive": false } );

        return () => {
            element.removeEventListener( "touchstart", handleTouchStart );
            element.removeEventListener( "touchmove", handleTouchMove );
        };
    }, [ elementRef, handleTouchMove, handleTouchStart, isEnabled ] );
};

